import React from 'react';
import {
    ArrowRightIcon,
     CheckboxChip,
} from '../../../../../components/buttons/IconButtons';
import CustomGenericTittleIconDataChip from '../../../../../components/CustomGenericTittleIconDataChip';
import {  useTranslation } from 'react-i18next';
import { ButtonLargeTransparent} from '../../../../../components/buttons/ButtonLargeTransparent';
import routes from '../../../../../Routes/routes';
import {getUrlParameters} from "../../../Visitor/External/Model/Model";
import {toast} from "react-toastify";
import {
    PublicCreateUserInvitations,
    PublicProcessCompleted
} from "../../../../../reduxToolkit/EmployeeEvents/EmployeeEventsApi";
import {updateVeridasUser} from "../../../../../reduxToolkit/Visitor/VisitorApi";
import {getFailedUrl} from "../../../../Veridas/Model/Models";
import {publicUpdateVisitorStatusByInvitationId} from "../../../../../reduxToolkit/Invitation/InvitationApi";
import {STATUS_MODEL} from "../../../../../enums/statusEnum";

/**
 * @Email : sgarcia@ibl.mx
 * @Author: Susana Garcia Rodriguez
 * @jobTittle: Frontend
 */

const ProceedWithRegistrationHeader = () => {
    return (
        <CustomGenericTittleIconDataChip Icon={CheckboxChip} title={'complete_your_verification'} border={false} className={"px-0 align-items-center"}/>
    );
};

const ProceedWithRegistrationBody = ({ id, }) => {
    const { t } = useTranslation();

    return (
        <>
            <div className='my-3'>
                <p className={'font-color-general-950 ibl-ientry-p ibl-font-w-400'}>{t('please_verify_your_identity_to_continue')}</p>
            </div>

            <div>
                <ButtonLargeTransparent
                    text={t('click_here')}
                    className='btn-next-step'
                    icon={<ArrowRightIcon/>}
                    iconColor='icon-color-general-00'
                    disabled={false}
                    url={getUrlParameters({
                        url:routes.eventDetails.findMySelf,
                        id
                    })}
                />
            </div>
        </>
    );
};

export const RESERVATION_STATUS_MODEL_CARD = {
    PROCEED_WITH_REGISTRATION: {//Guard flow
        ID: null,
        NAME: '',
        DIVIDER: true,
        HEADER: (props) => <ProceedWithRegistrationHeader />,
        BODY: ({ id }) => <ProceedWithRegistrationBody id={id}/>,
    },
};

export const EVENT_STEP_MODEL = {
    FIND_MYSELF: {
        TOTAL: 1,
        NAME: 'FIND_MYSELF'
    },
    FIND_MYSELF_CREATE_VISITOR: {
        TOTAL: 1,
        NAME: 'FIND_MYSELF_CREATE_VISITOR'
    },
    I_AM_NEW_HERE: {
        TOTAL: 1,
        NAME: 'I_AM_NEW_HERE'
    }
}


export const EVENT_VISITOR_MODEL = {
    eventId: null,
    visitorId: null,
    toRegister: function (eventId, visitorId) {
        return {
            eventId: eventId,
            visitors: [
                {
                    id: visitorId
                }
            ],
            employees: []
        }
    },
    toRegisterVerifyEmail: function ({ email, eventId }) {
        return {
            email: email,
            eventId: eventId
        };
    },
    isValid: function (eventId, visitorId) {
        return eventId !== null && visitorId !== null && visitorId !== undefined ;
    }
}

/**
 * Handles successful registration flow.
 *
 * @param {Object} params
 * @param {Function} params.setLoading - Function to update loading state.
 * @param {Function} params.t - Translation function.
 * @param {Function} params.navigate - React Router navigation function.
 * @param {string} params.successUrl - URL to redirect on success.
 */
const handleSuccess = ({
                           setLoading,
                           t,
                           navigate,
                           successUrl
                       }) => {

    setLoading(false);

    toast.success(
        t("has_been_registered_successfully")
    );

    navigate(successUrl);
};

/**
 * Handles failed registration flow and redirects to failure page.
 *
 * @param {Object} params
 * @param {Function} params.setLoading - Function to update loading state.
 * @param {Function} params.navigate - React Router navigation function.
 * @param {string|number} params.resolvedUserId - User identifier resolved during flow.
 * @param {string|number} params.eventId - Event identifier.
 */
const handleFailed = ({
                          setLoading,
                          navigate,
                          resolvedUserId,
                          eventId
                      }) => {

    setLoading(false);

    navigate(
        getFailedUrl({
            userId: resolvedUserId,
            eventId
        })
    );
};

/**
 * Registers a user in an event and executes post-registration processes.
 *
 * Flow:
 * 1. Creates invitation
 * 2. Updates invitation status if exists
 * 3. Marks process as completed
 * 4. Redirects to success page
 *
 * @async
 * @param {Object} params
 * @param {Function} params.dispatch - Redux dispatch function.
 * @param {string|number} params.resolvedUserId - User identifier.
 * @param {string|number} params.eventId - Event identifier.
 * @param {Function} params.setLoading - Function to update loading state.
 * @param {Function} params.t - Translation function.
 * @param {Function} params.navigate - React Router navigation function.
 * @param {string} params.successUrl - URL to redirect on success.
 */
export const registerInEvent = async ({
    dispatch,
    resolvedUserId,
    eventId,
    setLoading,
    t,
    navigate,
    successUrl
}) => {
    try {
        const body = EVENT_VISITOR_MODEL.toRegister(
            eventId,
            resolvedUserId
        );

        const {
            payload: {
                data: {
                    data,
                    success
                }
            }
        } = await dispatch(
            PublicCreateUserInvitations(body)
        );

        if (!success) {
            throw new Error("create_invitation_failed");
        }

        const invitationId = data?.[0]?.id;

        if (!invitationId) {
            handleSuccess({
                setLoading,
                t,
                navigate,
                successUrl
            });
            return;
        }

        await updateInvitationStatus({
            dispatch,
            invitationId
        });

        await processCompleted({
            dispatch,
            resolvedUserId
        });

        handleSuccess({
            setLoading,
            t,
            navigate,
            successUrl
        });

    } catch (error) {
        console.error(error);
        handleFailed({
            setLoading,
            navigate,
            resolvedUserId,
            eventId
        });
    }
};

/**
 * Updates invitation status to ACCEPTED.
 *
 * @async
 * @param {Object} params
 * @param {Function} params.dispatch - Redux dispatch function.
 * @param {string|number} params.invitationId - Invitation identifier.
 * @throws {Error} If update fails.
 */
const updateInvitationStatus = async ({
    dispatch,
    invitationId
}) => {

    const {
        payload: {
            data: { success }
        }
    } = await dispatch(
        publicUpdateVisitorStatusByInvitationId({
            invitationId,
            statusId: STATUS_MODEL.ACCEPTED
        })
    );

    if (!success) {
        throw new Error(
            "update_invitation_status_failed"
        );
    }
};

/**
 * Marks registration process as completed for a user.
 *
 * @async
 * @param {Object} params
 * @param {Function} params.dispatch - Redux dispatch function.
 * @param {string|number} params.resolvedUserId - User identifier.
 * @throws {Error} If process completion fails.
 */
const processCompleted = async ({
    dispatch,
    resolvedUserId
}) => {

    const {
        payload: {
            data: { success }
        }
    } = await dispatch(
        PublicProcessCompleted(
            resolvedUserId
        )
    );

    if (!success) {
        throw new Error(
            "process_completed_failed"
        );
    }
};

/**
 * Handles Veridas validation + event registration flow.
 *
 * @param {Object} params
 * @param {Function} params.dispatch - Redux dispatch function.
 * @param {string|number} params.resolvedUserId - User identifier.
 * @param {string|number} params.eventId - Event identifier.
 * @param {Function} params.setLoading - Loading state setter.
 * @param {Function} params.t - Translation function.
 * @param {Function} params.navigate - Router navigation function.
 * @param {string} params.successUrl - Success redirect URL.
 * @param {Object} params.data - Validation response data.
 * @param {string|number} params.validationId - Veridas validation identifier.
 */
export const registerInEventVeridas = ({
    dispatch,
    resolvedUserId,
    eventId,
    setLoading,
    t,
    navigate,
    successUrl,
    data,
    validationId
}) => {

    dispatch(
        updateVeridasUser({
            userId: resolvedUserId,
            validationId,
            ocrAndScoresResponse: data,
        })
    ).then(({ payload: { data: { data } } }) => {

        if (data) {
            registerInEvent({
                dispatch,
                resolvedUserId,
                eventId,
                setLoading,
                t,
                navigate,
                successUrl
            });
        } else {
            navigate(getFailedUrl({userId:resolvedUserId, eventId}));
        }
    });
};
